import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  BanknotesIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ModernProjectSelectorField } from "../../../components/utils/moduleSelectors/ModernProjectSelectorField";
import { triggerProjectAccountingPdfDownload } from "../../../components/dashboard/accounting/project-accounting-print-content";
import { LaborSettlementDetails } from "../../../components/dashboard/accounting/LaborSettlementDetails";
import { fetchAccountingProjectSummary, downloadAccountingDebugExport } from "../../../api/accounting";
import { updateUrlWithException } from "../../../components/Utils";
import { signedFinancialColorClass } from "../../../utils/signedFinancialColor";

const formatCurrency = (value) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatPercentage = (value) => `${(parseFloat(value) || 0).toFixed(1)}%`;

const financialColor = (value) =>
  signedFinancialColorClass(value, { neutralClass: "text-gray-900" });

function readInitialProjectId() {
  if (typeof window === "undefined") return null;
  const sp = new URLSearchParams(window.location.search);
  const id = sp.get("project_id");
  return id ? parseInt(id, 10) : null;
}

const MetricCard = ({ icon: Icon, label, value, hint, colorClass = "text-gray-900" }) => (
  <div className="bg-white overflow-hidden shadow rounded-lg">
    <div className="p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className="h-6 w-6 text-blue-400" />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
            <dd className={`text-lg font-medium ${colorClass}`}>{value}</dd>
            {hint ? <dd className="text-xs text-gray-500 mt-1">{hint}</dd> : null}
          </dl>
        </div>
      </div>
    </div>
  </div>
);

export const AccountingProjectSummaryPage = () => {
  const { t, i18n } = useTranslation();
  const [selectedProject, setSelectedProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [debugExportLoading, setDebugExportLoading] = useState(false);

  const loadSummary = useCallback(async (projectId) => {
    if (!projectId) {
      setSummary(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchAccountingProjectSummary({ projectId, includeModules: true });
      if (result.error) {
        setError(result.data?.errors?.[0] || t("accounting.project_summary.load_error"));
        setSummary(null);
      } else {
        setSummary(result.data);
      }
    } catch (err) {
      console.error(err);
      setError(t("accounting.project_summary.load_error"));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const initialId = readInitialProjectId();
    if (initialId) {
      setSelectedProject({ id: initialId });
      loadSummary(initialId);
    }
  }, [loadSummary]);

  const handleProjectChange = (event) => {
    const project = event?.target?.object || null;
    setSelectedProject(project);
    if (project?.id) {
      updateUrlWithException({ project_id: project.id });
      loadSummary(project.id);
    } else {
      setSummary(null);
    }
  };

  const labor = summary?.labor || {};
  const income = summary?.income || {};

  const contractSumSourceKey = {
    contract_lines: "accounting.project_summary.contract_sum_source_lines",
    contract_agreement: "accounting.project_summary.contract_sum_source_agreement",
    contract_lines_and_agreement: "accounting.project_summary.contract_sum_source_both",
    none: "accounting.project_summary.contract_sum_source_none",
  }[summary?.contract_sum_source] || "accounting.project_summary.contract_sum_source_none";

  const handleDownloadPdf = async () => {
    if (!summary?.project_id) return;
    setPdfLoading(true);
    await triggerProjectAccountingPdfDownload({
      projectId: summary.project_id,
      projectNumber: summary.project_number,
      summary,
      locale: i18n.language,
      includeModules: true,
      onError: (message) => setError(message),
    });
    setPdfLoading(false);
  };

  const handleDownloadDebugExport = async () => {
    if (!summary?.project_id) return;
    setDebugExportLoading(true);
    try {
      await downloadAccountingDebugExport({
        projectId: summary.project_id,
        year: new Date().getFullYear(),
        filename: `accounting-debug-${summary.project_number || summary.project_id}-${new Date().getFullYear()}.json`,
      });
    } catch (err) {
      setError(err.message || t("accounting.project_summary.debug_export.error"));
    } finally {
      setDebugExportLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div>
        <DashboardHeader
          title={t("accounting.project_summary.title")}
          subtitle={t("accounting.project_summary.subtitle")}
          icon={BanknotesIcon}
        />
      </div>

      <div className="mx-auto max-w-7xl w-full px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600 mb-4">
              {t("accounting.project_summary.description")}
            </p>
            <ModernProjectSelectorField
              name="project_id"
              label={t("accounting.project_summary.select_project")}
              selected={selectedProject}
              onChange={handleProjectChange}
              emptyDisplayText={t("accounting.project_summary.select_project_placeholder")}
            />
          </section>

          {loading && (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}

          {error && !loading && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 mx-auto text-red-400 mb-3" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {summary && !loading && (
            <>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {summary.project_name}
                    {summary.project_number ? (
                      <span className="text-gray-500 font-normal ml-2">#{summary.project_number}</span>
                    ) : null}
                  </h2>
                  {summary.customer_name ? (
                    <p className="text-sm text-gray-600 mt-1">{summary.customer_name}</p>
                  ) : null}
                  <p className="text-sm text-gray-500 mt-1">
                    {t("accounting.project_summary.last_updated")}:{" "}
                    {new Date(summary.last_updated).toLocaleString("no-NO")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleDownloadDebugExport}
                    disabled={debugExportLoading}
                    className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-800 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-60"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {debugExportLoading
                      ? t("accounting.project_summary.debug_export.loading")
                      : t("accounting.project_summary.debug_export.button")}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={pdfLoading}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {pdfLoading
                      ? t("project.overview.export_pdf_loading")
                      : t("accounting.project_summary.print.button")}
                  </button>
                  <Link
                    to={`/dashboard/projects/${summary.project_id}`}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t("accounting.project_summary.open_project")}
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                <MetricCard
                  icon={CurrencyDollarIcon}
                  label={t("accounting.project_summary.contract_sum")}
                  value={formatCurrency(summary.contract_sum)}
                  hint={t(contractSumSourceKey)}
                  colorClass={financialColor(summary.contract_sum)}
                />
                <MetricCard
                  icon={DocumentTextIcon}
                  label={t("accounting.project_summary.invoiced")}
                  value={formatCurrency(summary.invoiced_amount)}
                  hint={
                    (summary.customer_invoice_count || 0) > 0 || (summary.invoiced_amount || 0) !== 0
                      ? t("accounting.project_summary.invoiced_ex_vat")
                      : t("accounting.project_summary.not_invoiced")
                  }
                  colorClass={financialColor(summary.invoiced_amount)}
                />
                <MetricCard
                  icon={BanknotesIcon}
                  label={t("accounting.project_summary.total_costs")}
                  value={formatCurrency(summary.total_costs)}
                  colorClass={financialColor(summary.total_costs)}
                />
                <MetricCard
                  icon={ChartBarIcon}
                  label={t("accounting.project_summary.profit_vs_contract")}
                  value={formatCurrency(summary.profit_vs_contract ?? summary.profit)}
                  hint={`${t("accounting.project_summary.margin_vs_contract")}: ${formatPercentage(summary.margin_vs_contract ?? summary.margin)}`}
                  colorClass={financialColor(summary.profit_vs_contract ?? summary.profit)}
                />
                <MetricCard
                  icon={ChartBarIcon}
                  label={t("accounting.project_summary.profit_vs_invoiced")}
                  value={formatCurrency(summary.profit_vs_invoiced ?? summary.provisional_result)}
                  hint={
                    (summary.customer_invoice_count || 0) > 0 && (summary.invoiced_amount || 0) > 0
                      ? `${t("accounting.project_summary.margin_vs_invoiced")}: ${formatPercentage(summary.margin_vs_invoiced)}`
                      : t("accounting.project_summary.not_invoiced")
                  }
                  colorClass={financialColor(summary.profit_vs_invoiced ?? summary.provisional_result)}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("accounting.project_summary.income_section")}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("accounting.project_summary.contract_sum")}</span>
                      <span className={`font-medium ${financialColor(summary.contract_sum)}`}>{formatCurrency(summary.contract_sum)}</span>
                    </div>
                    {summary.contract_agreement_price > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t("accounting.project_summary.contract_agreement_price")}</span>
                        <span className={`font-medium ${financialColor(summary.contract_agreement_price)}`}>{formatCurrency(summary.contract_agreement_price)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("accounting.project_summary.invoiced")}</span>
                      <span className={`font-medium ${financialColor(income.invoiced)}`}>
                        {(summary.customer_invoice_count || 0) > 0 || (income.invoiced || 0) !== 0
                          ? formatCurrency(income.invoiced)
                          : t("accounting.project_summary.not_invoiced")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("accounting.project_summary.expected_module_income")}</span>
                      <span className={`font-medium ${financialColor(income.expected_from_modules)}`}>{formatCurrency(income.expected_from_modules)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium text-gray-900">{t("accounting.project_summary.income_total")}</span>
                      <span className={`font-medium ${financialColor(income.total_invoiced_and_expected)}`}>{formatCurrency(income.total_invoiced_and_expected)}</span>
                    </div>
                  </div>
                </section>

                <section className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("accounting.project_summary.costs_section")}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="rounded-lg bg-blue-50 p-3 mb-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">{t("accounting.project_summary.labor_costs")}</span>
                        <span className={`font-semibold ${financialColor(labor.costs)}`}>{formatCurrency(labor.costs)}</span>
                      </div>
                      <LaborSettlementDetails
                        labor={labor}
                        t={t}
                        formatCurrency={formatCurrency}
                        translationPrefix="accounting.project_summary"
                      />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 flex items-center gap-2">
                        <ClipboardDocumentListIcon className="h-4 w-4" />
                        {t("accounting.project_summary.material_costs")}
                      </span>
                      <span className={`font-medium ${financialColor(summary.material_costs)}`}>{formatCurrency(summary.material_costs)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 flex items-center gap-2">
                        <TruckIcon className="h-4 w-4" />
                        {t("accounting.project_summary.supplier_invoices")}
                      </span>
                      <span className={`font-medium ${financialColor(summary.supplier_invoices)}`}>{formatCurrency(summary.supplier_invoices)}</span>
                    </div>
                    {summary.voucher_expenses > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t("accounting.project_summary.voucher_expenses")}</span>
                        <span className={`font-medium ${financialColor(summary.voucher_expenses)}`}>{formatCurrency(summary.voucher_expenses)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium text-gray-900">{t("accounting.project_summary.total_costs")}</span>
                      <span className={`font-medium ${financialColor(summary.total_costs)}`}>{formatCurrency(summary.total_costs)}</span>
                    </div>
                  </div>
                </section>
              </div>

              {summary.modules?.length > 0 && (
                <section className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("accounting.project_summary.modules_section")}
                  </h3>
                  <div className="space-y-3">
                    {summary.modules.map((mod) => (
                      <div key={`${mod.module_type}-${mod.id}`} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start gap-4">
                          <div>
                            <span className="text-xs uppercase text-gray-500">{mod.module_type}</span>
                            <Link to={mod.link} className="block font-medium text-gray-900 hover:text-blue-600">
                              {mod.display_title}
                            </Link>
                          </div>
                          <div className="text-right text-sm">
                            <div className={financialColor(mod.income?.total)}>{formatCurrency(mod.income?.total)}</div>
                            <div className={financialColor(mod.costs?.total)}>{formatCurrency(mod.costs?.total)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {!selectedProject && !loading && (
            <div className="text-center py-16 text-gray-500">
              <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t("accounting.project_summary.select_project_hint")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountingProjectSummaryPage;
